/** Pure path updates for FlowV2 folder-tree reparent. */

export function joinPath(parent, name) {
    const path = parent == null ? '' : String(parent);
    const itemName = name == null ? '' : String(name);
    if (!path)
        return itemName;
    if (!itemName)
        return path;
    return `${path}/${itemName}`;
}

export function itemFullPath(item) {
    if (!item)
        return '';
    if (item.Type === 'Folder' && item.ID)
        return item.ID;
    return joinPath(item.Path || '', item.Name || '');
}

function hasPrefix(value, prefix) {
    if (!prefix || typeof value !== 'string')
        return false;
    return value === prefix || value.startsWith(prefix + '/');
}

function swapPrefix(value, oldPrefix, newPrefix) {
    if (value === oldPrefix)
        return newPrefix || '';
    if (oldPrefix && typeof value === 'string' && value.startsWith(oldPrefix + '/'))
        return (newPrefix || '') + value.slice(oldPrefix.length);
    return value;
}

/**
 * New Path field for the dragged row.
 * Root and folder targets yield a parent folder path.
 * Project and class targets yield that item's full path.
 */
export function newPathForTarget(target) {
    if (!target || target.Type === 'root')
        return '';
    if (target.Type === 'Folder')
        return itemFullPath(target);
    return itemFullPath(target);
}

/**
 * @returns {Array<{ oldId: string, row: object }>}
 */
export function repathSubtree(rows, dragged, newPath) {
    const list = Array.isArray(rows) ? rows : [];
    const path = newPath || '';
    const updates = [];
    if (!dragged?.Type)
        return updates;

    if (dragged.Type === 'Folder') {
        const oldFull = itemFullPath(dragged);
        const newFull = joinPath(path, dragged.Name);
        if (oldFull === newFull)
            return updates;
        for (const row of list) {
            if (row.Type === 'Folder') {
                const id = row.ID || itemFullPath(row);
                if (!hasPrefix(id, oldFull))
                    continue;
                const rowPath = row.Path || '';
                let nextPath = rowPath;
                if (id === oldFull)
                    nextPath = path;
                else if (hasPrefix(rowPath, oldFull))
                    nextPath = swapPrefix(rowPath, oldFull, newFull);
                updates.push({
                    oldId: row.ID,
                    row: { ...row, ID: swapPrefix(id, oldFull, newFull), Path: nextPath },
                });
                continue;
            }
            const rowPath = row.Path || '';
            if (!hasPrefix(rowPath, oldFull))
                continue;
            updates.push({
                oldId: row.ID,
                row: { ...row, Path: swapPrefix(rowPath, oldFull, newFull) },
            });
        }
        return updates;
    }

    if (dragged.Type === 'Project') {
        const oldFull = itemFullPath(dragged);
        const newFull = joinPath(path, dragged.Name);
        if ((dragged.Path || '') === path)
            return updates;
        for (const row of list) {
            if (row.ID === dragged.ID) {
                updates.push({ oldId: row.ID, row: { ...row, Path: path } });
                continue;
            }
            if (row.Type === 'Folder')
                continue;
            const rowPath = row.Path || '';
            if (!hasPrefix(rowPath, oldFull))
                continue;
            updates.push({
                oldId: row.ID,
                row: { ...row, Path: swapPrefix(rowPath, oldFull, newFull) },
            });
        }
        return updates;
    }

    if (dragged.Type === 'Class') {
        const oldFull = itemFullPath(dragged);
        const newFull = joinPath(path, dragged.Name);
        if ((dragged.Path || '') === path)
            return updates;
        for (const row of list) {
            if (row.ID === dragged.ID) {
                updates.push({ oldId: row.ID, row: { ...row, Path: path } });
                continue;
            }
            if ((row.Type === 'FlowHeader' || row.Type === 'APIHeader') && (row.Path || '') === oldFull)
                updates.push({ oldId: row.ID, row: { ...row, Path: newFull } });
        }
        return updates;
    }

    if (dragged.Type === 'FlowHeader' || dragged.Type === 'APIHeader') {
        if ((dragged.Path || '') === path)
            return updates;
        const source = list.find(row => row.ID === dragged.ID) || dragged;
        updates.push({ oldId: dragged.ID, row: { ...source, Path: path } });
    }
    return updates;
}

export function destinationNameTaken(rows, name, parentPath, excludeIds) {
    const parent = parentPath || '';
    const skip = excludeIds || new Set();
    return (rows || []).some(row => !skip.has(row.ID) && (row.Path || '') === parent && row.Name === name);
}
